/**
 * Rhythmz — AIDiagnostics
 * ────────────────────────
 * Real, human-interpretable data analysis. Replaces generic status icons
 * with actual analysis of audio, visual, URL, and system state.
 *
 * Every function returns a plain English string that describes exactly
 * what's happening, what the data means, and what the AI will do with it.
 *
 * No "🧠 Thinking…" bullshit. Real data only.
 */

import type { AudioFeatures } from '../engine/AudioAnalyzer';
import { captureFrame, type FrameSnapshot } from './SceneSnapshotter';

/* ─────────────── Audio Analysis ─────────────── */

export interface AudioReport {
  summary: string;        // one-line human summary
  genreGuess: string;     // what genre the audio resembles
  bpmEstimate: number;
  dominantBand: string;   // bass / lowMid / mid / highMid / treble
  energy: number;         // 0-100
  beatStrength: number;   // 0-100
  isBeating: boolean;
  recommendations: string[];
}

export function analyzeAudio(features: AudioFeatures): AudioReport {
  const { bass, lowMid, mid, highMid, treble, level, beat, bpm } = features;

  // Determine dominant frequency band
  const bands = { bass, lowMid, mid, highMid, treble };
  const dominantBand = Object.entries(bands).reduce((a, b) => (b[1] > a[1] ? b : a))[0];

  // Energy level
  const energy = Math.round(level * 100);

  // Beat analysis
  const beatStrength = Math.round(beat * 100);
  const isBeating = beat > 0.3;

  // Genre guessing from spectral profile
  const genreGuess = guessGenre(bass, mid, treble, level, bpm);

  // Build recommendations
  const recommendations: string[] = [];

  if (isBeating && bpm > 100 && bass > 0.3) {
    recommendations.push('Strong bass + high BPM — try a torus knot with pulse animation');
  }
  if (treble > 0.4 && mid < 0.2) {
    recommendations.push('Treble-heavy sound — light beams or particle effects would match well');
  }
  if (level < 0.05) {
    recommendations.push('Audio is silent or very quiet — AI can compose its own music to drive visuals');
  }
  if (bpm > 130) {
    recommendations.push('Fast tempo — switch to FractalTunnel or KaleidoscopicTorus for high-energy rendering');
  }
  if (bass > 0.4 && mid < 0.15) {
    recommendations.push('Sub-bass heavy audio — galaxy or void scene with deep bass-reactive colors');
  }
  if (mid > 0.3 && treble > 0.2) {
    recommendations.push('Balanced mid/treble spectrum — crystalline or geometric scenes with bright palette');
  }

  if (recommendations.length === 0) {
    recommendations.push('Standard audio — any scene type works, try auto-rotate for variety');
  }

  return {
    summary: `${genreGuess}, ${bpm > 0 ? `${Math.round(bpm)}bpm` : 'unknown tempo'}, ${dominantBand}-heavy, ${energy}% energy, ${isBeating ? `beat at ${beatStrength}%` : 'no detectable beat'}`,
    genreGuess,
    bpmEstimate: Math.round(bpm),
    dominantBand,
    energy,
    beatStrength,
    isBeating,
    recommendations,
  };
}

function guessGenre(bass: number, mid: number, treble: number, level: number, bpm: number): string {
  if (level < 0.02) return 'silence';

  // Spectral profiling
  if (bpm > 160 && bass > 0.3) return 'drum and bass';
  if (bpm > 125 && bass > 0.4) return 'techno';
  if (bpm > 120 && bass > 0.3 && mid > 0.2) return 'house';
  if (bpm > 90 && bpm < 120 && bass > 0.35) return 'hip-hop';
  if (bpm > 60 && bpm < 100 && mid > 0.25 && treble < 0.2) return 'lo-fi';
  if (bpm < 80 && treble > 0.3 && bass < 0.2) return 'ambient';
  if (bpm > 130 && treble > 0.35) return 'trance';
  if (bass > 0.5 && mid < 0.1) return 'sub-bass heavy / electronic';
  if (mid > 0.4 && treble > 0.3) return 'synthwave / pop';
  if (bass < 0.1 && mid < 0.15 && treble > 0.3) return 'hi-fi / treble dominant';
  if (level > 0.7) return 'loud / heavily compressed';
  return 'unknown / mixed';
}

/* ─────────────── Visual Analysis ─────────────── */

export interface VisualReport {
  summary: string;
  fillPercent: number;
  avgColor: [number, number, number]; // 0-1
  colorName: string;
  brightness: string;       // "very dark" / "dim" / "moderate" / "bright" / "blinding"
  sceneType: string;        // what the scene looks like
  objectCount: number;
  isRendering: boolean;
  recommendations: string[];
}

export function analyzeVisual(canvas: HTMLCanvasElement, objectCount: number): VisualReport | null {
  const snap = captureFrame(canvas);
  if (!snap) return null;

  const [r, g, b] = snap.avgColor;
  const fill = snap.fillPercent;

  // Color naming
  const colorName = nameColor(r, g, b);

  // Brightness
  const lum = 0.299 * r + 0.587 * g + 0.114 * b; // perceptual luminance
  const brightness = lum < 0.05 ? 'very dark / black' :
    lum < 0.15 ? 'dim / dark' :
    lum < 0.35 ? 'moderate' :
    lum < 0.6 ? 'bright' :
    'blinding / fully white';

  // Scene type guess
  const sceneType = fill < 2 ? 'empty / blank screen — no objects visible' :
    fill < 10 ? 'sparse — few objects or very dark scene' :
    fill < 30 ? 'moderately filled' :
    fill < 60 ? 'well populated scene' :
    'densely filled / bright scene';

  // Recommendations
  const recommendations: string[] = [];
  if (fill < 2) {
    recommendations.push('Scene is empty — use "build galaxy" or "build neon city" to add content');
    recommendations.push('Make sure audio is playing or the AI has composed music so there\'s something to visualize');
  }
  if (lum < 0.08) {
    recommendations.push('Too dark — increase emissive intensity, add point lights, or switch to a brighter scene like GeometricBloom');
  }
  if (objectCount === 0 && fill > 2) {
    recommendations.push('Objects count is 0 but there\'s content — background shader is running but no 3D objects. Use SceneToolkit to add geometry.');
  }
  if (objectCount > 0 && fill < 5) {
    recommendations.push('Objects exist but aren\'t visible — check camera position, object scale, or material transparency');
  }

  return {
    summary: `${sceneType}, ${colorName}, ${brightness}, ${objectCount} objects`,
    fillPercent: snap.fillPercent,
    avgColor: snap.avgColor,
    colorName,
    brightness,
    sceneType,
    objectCount,
    isRendering: snap.fillPercent > 0.5,
    recommendations,
  };
}

function nameColor(r: number, g: number, b: number): string {
  // Simple color naming based on dominant channels
  if (r < 0.05 && g < 0.05 && b < 0.05) return 'black';
  if (r > 0.8 && g < 0.2 && b < 0.2) return 'red';
  if (g > 0.6 && r < 0.2 && b < 0.2) return 'green';
  if (b > 0.6 && r < 0.2 && g < 0.2) return 'blue';
  if (r > 0.5 && g > 0.5 && b < 0.2) return 'yellow/amber';
  if (r > 0.5 && b > 0.5 && g < 0.2) return 'pink/magenta';
  if (g > 0.4 && b > 0.5) return 'cyan/teal';
  if (r > 0.5 && g > 0.3 && b > 0.3) return 'orange/warm';
  if (r > 0.3 && g > 0.3 && b > 0.3) return 'neutral gray/white';
  if (r < 0.15 && g < 0.15 && b > 0.3) return 'dark blue';
  if (r > 0.3 && g > 0.3 && b < 0.1) return 'dark yellow/brown';
  return 'mixed';
}

/* ─────────────── URL/Link Analysis ─────────────── */

export interface LinkReport {
  url: string;
  title: string;
  contentType: string;     // "spotify track", "github repo", "youtube", "article", "image", "unknown"
  relevance: string;       // "highly relevant" / "somewhat relevant" / "not relevant" / "unknown"
  summary: string;         // what the link contains
  implementationPlans: string[]; // how the AI will use this info
  metadata: Record<string, string>;
}

/**
 * Analyze a URL to determine if it's relevant to Rhythmz
 * and explain how the AI will use the content.
 */
export async function analyzeLink(url: string): Promise<LinkReport> {
  const result: LinkReport = {
    url,
    title: '',
    contentType: 'unknown',
    relevance: 'unknown',
    summary: '',
    implementationPlans: [],
    metadata: {},
  };

  // Detect content type from URL pattern
  if (url.includes('spotify.com/track/')) {
    result.contentType = 'spotify track';
    // Extract track ID
    const trackId = url.match(/track\/([a-zA-Z0-9]+)/)?.[1] ?? '';
    result.metadata.trackId = trackId;

    // Try to fetch metadata
    try {
      const resp = await fetch(url);
      const html = await resp.text();
      const titleMatch = html.match(/<title>([^<]+)<\/title>/);
      if (titleMatch) {
        result.title = titleMatch[1].replace(' | Spotify', '').trim();
      }
      result.summary = `Spotify track: ${result.title}. Fetch the preview/metadata to analyze tempo, genre, mood for AI music learning.`;
      result.relevance = 'highly relevant';
      result.implementationPlans = [
        'Extract audio features (BPM, key, mood) from the track metadata',
        'Build a sonic profile that the AI can use to compose similar music',
        'Match visual scene parameters to the track\'s energy and mood',
        'Add to the MusicLearner reference library for genre-specific composition',
      ];
    } catch {
      result.summary = `Spotify track at ${url} — unable to fetch metadata directly (requires auth). AI can still analyze the track by name/genre if provided.`;
      result.relevance = 'somewhat relevant';
      result.implementationPlans = [
        'Use the track name/artist to search for genre and style info',
        'Build a sonic profile entry with estimated parameters',
      ];
    }
  } else if (url.includes('github.com')) {
    result.contentType = 'github repository';
    const repoPath = url.replace('https://github.com/', '').split('/').slice(0, 2).join('/');
    result.metadata.repo = repoPath;
    result.title = repoPath;

    // Try to fetch repo info
    try {
      const resp = await fetch(`https://api.github.com/repos/${repoPath}`);
      if (resp.ok) {
        const data: any = await resp.json();
        result.title = data.full_name ?? repoPath;
        result.summary = `GitHub repo: ${data.description ?? 'No description'}. ${data.stargazers_count ?? 0} stars. ${data.language ?? 'Unknown language'}.`;
        if ((data.description ?? '').toLowerCase().includes('visualizer') ||
            (data.description ?? '').toLowerCase().includes('webgpu') ||
            (data.description ?? '').toLowerCase().includes('three.js') ||
            (data.description ?? '').toLowerCase().includes('audio') ||
            (data.description ?? '').toLowerCase().includes('shader')) {
          result.relevance = 'highly relevant';
          result.implementationPlans = [
            'Study the codebase for rendering techniques the AI can replicate',
            'Extract shader code, scene setups, or audio processing techniques',
            'Port relevant algorithms into the SceneToolkit or VisualizerEngine',
          ];
        } else {
          result.relevance = 'somewhat relevant';
          result.implementationPlans = [
            'Review the codebase for any applicable patterns or techniques',
            'Skip if no visual/audio relevance found',
          ];
        }
      }
    } catch {
      result.summary = `GitHub repo at ${url} — unable to fetch details.`;
    }
  } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
    result.contentType = 'youtube video';
    result.title = url.split('/').pop() ?? 'video';
    result.summary = `YouTube video — audio from the description/sound design could be analyzed for spectral patterns and genre classification.`;
    result.relevance = 'somewhat relevant';
    result.implementationPlans = [
      'If audio extraction is possible, analyze the sound for BPM, key, genre',
      'Use the video metadata (title, description) for mood/scene guidance',
      'Consider the visual style of the thumbnail for scene inspiration',
    ];
  } else if (url.match(/\.(png|jpg|jpeg|gif|webp|svg)$/i)) {
    result.contentType = 'image';
    result.title = url.split('/').pop() ?? 'image';
    result.summary = `Image file — visual style could be analyzed for color palette, composition, and mood to guide scene generation.`;
    result.relevance = 'somewhat relevant';
    result.implementationPlans = [
      'Analyze dominant colors to generate a matching palette',
      'Use image mood for scene atmosphere recommendations',
      'Extract geometric patterns for scene structure ideas',
    ];
  } else {
    result.contentType = 'web page';
    result.title = url.split('/').pop()?.split('?')[0] ?? 'page';

    // Generic web fetch
    try {
      const resp = await fetch(url, { headers: { 'user-agent': 'RhythmzBot/1.0' } });
      if (resp.ok) {
        const html = await resp.text();
        const titleMatch = html.match(/<title[^>]*>([^<]+)<\/title>/);
        if (titleMatch) result.title = titleMatch[1].trim();

        // Check for relevance keywords
        const bodyText = html.replace(/<[^>]+>/g, ' ').toLowerCase();
        const relevanceScore = ['visualizer', 'webgpu', 'three.js', 'shader', '3d', 'audio', 'music', 'sound',
          'visual', 'generative', 'creative coding', 'glsl', 'wgsl', 'tone.js', 'synthesizer',
          'daw', 'drum', 'bass', 'bpm', 'reverb', 'compression', 'midi', 'vst',
        ].filter(kw => bodyText.includes(kw)).length;

        result.summary = `Web page: "${result.title}". Contains ${relevanceScore} Rhythmz-relevant keywords.`;
        result.relevance = relevanceScore >= 5 ? 'highly relevant' :
          relevanceScore >= 2 ? 'somewhat relevant' : 'not relevant';

        if (relevanceScore >= 2) {
          result.implementationPlans = [
            'Extract technical concepts mentioned on the page',
            'Search for code examples or API references to incorporate',
            'Use descriptive text for scene/music mood guidance',
          ];
        } else {
          result.implementationPlans = [
            'Page has low Rhythmz relevance — may ignore or briefly summarize',
          ];
        }
      }
    } catch {
      result.summary = `Unable to fetch content from ${url}.`;
    }
  }

  return result;
}

/* ─────────────── System Status ─────────────── */

export interface SystemReport {
  summary: string;
  modelStatus: string;
  musicPlaying: boolean;
  musicBpm: number;
  sceneObjects: number;
  sceneName: string;
  audioEnergy: number;
  audioBpm: number;
  audioDominant: string;
  frameFill: number;
  frameColor: string;
  memoriesCount: number;
  profilesCount: number;
}

export function generateSystemReport(
  modelStatus: string,
  musicPlaying: boolean,
  musicBpm: number,
  sceneObjects: number,
  sceneName: string,
  audio: AudioFeatures,
  visualSnapshot: FrameSnapshot | null,
  profilesCount: number,
): SystemReport {
  const audioReport = analyzeAudio(audio);

  return {
    summary: `Rhythmz system: ${modelStatus}. ${musicPlaying ? `Music playing at ${musicBpm}bpm` : 'Music not playing'}. Scene "${sceneName}" with ${sceneObjects} objects. Audio: ${audioReport.summary}. Visual: ${visualSnapshot ? `Fill ${visualSnapshot.fillPercent.toFixed(1)}%` : 'No frame captured'}. ${profilesCount} learned profiles.`,
    modelStatus,
    musicPlaying,
    musicBpm,
    sceneObjects,
    sceneName,
    audioEnergy: audioReport.energy,
    audioBpm: audioReport.bpmEstimate,
    audioDominant: audioReport.dominantBand,
    frameFill: visualSnapshot?.fillPercent ?? 0,
    frameColor: visualSnapshot ? nameColor(visualSnapshot.avgColor[0], visualSnapshot.avgColor[1], visualSnapshot.avgColor[2]) : 'unknown',
    memoriesCount: profilesCount,
    profilesCount,
  };
}
